/** Agenda list (events grouped by day) and the compact event row used on the home screen. */
import { ChevronRight, MapPin, MessageCircle } from 'lucide-react';
import { Link } from 'react-router';
import type { EventDTO } from '../../../shared/dto/events';
import { useRequiredMe } from '../../auth/AuthProvider';
import { cx } from '../../components/ui';
import { useI18n, useT } from '../../i18n';
import { dayHeading, formatTime, groupByDay } from '../../lib/dates';
import { RsvpChip } from './participation';
import { canParticipate, KindIcon } from './shared';

export function EventRow({ ev }: { ev: EventDTO }) {
  const t = useT();
  const { loc } = useI18n();
  const me = useRequiredMe();
  const cancelled = ev.status === 'cancelled';
  return (
    <Link
      to={`/events/${ev.id}`}
      className={cx(
        'flex min-h-[72px] items-center gap-3 rounded-2xl border border-[var(--line)] bg-[var(--surface)] px-3 py-2.5 transition active:bg-[var(--accent-soft)]',
        cancelled && 'opacity-80',
      )}
    >
      <div className="w-[60px] shrink-0 text-center tabular-nums">
        <div className={cx('text-[19px] font-bold', cancelled && 'text-[var(--muted)] line-through')}>{formatTime(ev.startsAt, loc)}</div>
        <div className="text-[14px] text-[var(--muted)]">{formatTime(ev.endsAt, loc)}</div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <KindIcon kind={ev.kind} className={cx('size-5', cancelled ? 'text-[var(--muted)]' : 'text-[var(--accent)]')} />
          <span className={cx('truncate text-[17px] font-semibold', cancelled && 'text-[var(--muted)] line-through')}>{ev.title}</span>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[15px] text-[var(--muted)]">
          {cancelled ? (
            <span className="rounded-full bg-[var(--danger)] px-2 py-0.5 text-[13px] font-bold text-white no-underline">{t('calendar.event.cancelled')}</span>
          ) : null}
          {ev.location ? (
            <span className="inline-flex min-w-0 items-center gap-1">
              <MapPin className="size-4 shrink-0" aria-hidden />
              <span className="truncate">{ev.location}</span>
            </span>
          ) : null}
          {ev.commentCount > 0 ? (
            <span className={cx('inline-flex items-center gap-1', ev.unreadCount > 0 && 'font-bold text-[var(--danger-text)]')}>
              <MessageCircle className="size-4" aria-hidden />
              {ev.unreadCount > 0 ? t('calendar.event.unread', { count: ev.unreadCount }) : ev.commentCount}
            </span>
          ) : null}
        </div>
      </div>
      {!cancelled && canParticipate(ev, me.section) ? <RsvpChip ev={ev} className="hidden min-[400px]:inline-flex" /> : null}
      <ChevronRight className="size-5 shrink-0 text-[var(--muted)]" aria-hidden />
    </Link>
  );
}

/** Events grouped under day headings ("Í dag", "Á morgun", "Miðvikudagur 21. október"). */
export function AgendaList({ events, today, empty }: { events: EventDTO[]; today: string; empty: string }) {
  const { loc } = useI18n();
  if (events.length === 0) {
    return <p className="rounded-2xl border border-dashed border-[var(--line)] px-4 py-8 text-center text-[var(--muted)]">{empty}</p>;
  }
  const groups = groupByDay(events, (e) => e.startsAt);
  return (
    <div className="flex flex-col gap-5">
      {[...groups].map(([day, list]) => (
        <section key={day} aria-label={dayHeading(day, today, loc)}>
          <h3 className={cx('mb-2 px-1 text-[16px] font-bold', day === today ? 'text-[var(--accent)]' : 'text-[var(--ink-2)]')}>
            {dayHeading(day, today, loc)}
          </h3>
          <ul className="flex flex-col gap-2">
            {list.map((ev) => (
              <li key={ev.id}>
                <EventRow ev={ev} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
